/**
 * CR338: Ribbon Debug Sample Card
 *
 * Renders a mini V2 card focused on the ribbon, alongside a key-value
 * data panel listing every resolved input (text, color mode, effective
 * color, asset info, tint source, diagnostics warnings).
 *
 * Uses the same renderer helpers as live cards — no mock logic.
 */

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Info } from "lucide-react";
import {
  resolveRibbonColor,
  getResolvedRibbonHex,
  DEAL_TYPE_DEFAULT_HEX,
  type RibbonColorConfig,
  type RibbonColorSource,
} from "@/lib/deal-cards/resolveRibbonColor";
import {
  buildRibbonViewModel,
  type RibbonComponentConfig,
} from "@/lib/deal-cards/ribbonViewModel";
import { normalizeHex, isValidHex } from "@/lib/deals/hexColorUtils";
import type { DealCategory } from "@/lib/deals/dealTypeRegistry";

// ================================================================
// TYPES
// ================================================================

export interface RibbonDebugSampleCardProps {
  /** Current deal type key */
  dealTypeKey: string;
  /** Ribbon component config (from ComponentsConfig.ribbon) */
  ribbonComponentConfig?: RibbonComponentConfig | null;
  /** CR337 color config */
  colorConfig: RibbonColorConfig;
  /** Whether a ribbon asset graphic is selected */
  graphicSource: "system" | "asset";
  /** Selected asset ID (if any) */
  assetId?: string | null;
  /** Asset default tint from Asset Studio (if any) */
  assetDefaultTint?: string | null;
  /** Asset name for display */
  assetName?: string | null;
  /** Sample deal for preview (optional) */
  sampleDeal?: Record<string, unknown> | null;
}

// ================================================================
// DIAGNOSTIC RULES
// ================================================================

interface DiagnosticWarning {
  message: string;
  severity: "warn" | "info";
}

function buildDiagnostics(
  colorConfig: RibbonColorConfig,
  graphicSource: "system" | "asset",
  assetId: string | null | undefined,
  assetDefaultTint: string | null | undefined,
): DiagnosticWarning[] {
  const warnings: DiagnosticWarning[] = [];

  // Custom HEX selected but invalid / empty
  if (colorConfig.color_source === "custom_hex") {
    if (!colorConfig.color_hex || !isValidHex(colorConfig.color_hex)) {
      warnings.push({
        message: "Using theme default color; custom hex is invalid or empty.",
        severity: "warn",
      });
    }
  }

  // Asset tint selected but no asset chosen
  if (colorConfig.color_source === "asset_default") {
    if (graphicSource !== "asset" || !assetId) {
      warnings.push({
        message:
          "Asset Studio Tint selected, but no ribbon asset chosen – falling back to theme default.",
        severity: "warn",
      });
    } else if (!assetDefaultTint) {
      warnings.push({
        message:
          "Selected ribbon asset has no default_tint – falling back to theme default.",
        severity: "warn",
      });
    }
  }

  // Asset graphic selected but asset ID is missing
  if (graphicSource === "asset" && !assetId) {
    warnings.push({
      message: "Asset graphic source selected but no asset ID provided.",
      severity: "warn",
    });
  }

  if (warnings.length === 0) {
    warnings.push({
      message: "All ribbon inputs resolved correctly.",
      severity: "info",
    });
  }

  return warnings;
}

// ================================================================
// DATA ROW COMPONENT
// ================================================================

function DataRow({
  label,
  value,
  mono = false,
  colorSwatch,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  colorSwatch?: string | null;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10px] text-muted-foreground shrink-0">
        {label}
      </span>
      <div className="flex items-center gap-1.5 min-w-0">
        {colorSwatch && (
          <div
            className="w-3 h-3 rounded-sm border border-border shrink-0"
            style={{ backgroundColor: colorSwatch }}
          />
        )}
        <span
          className={cn(
            "text-[11px] truncate text-right",
            mono && "font-mono",
            !value && "text-muted-foreground italic",
          )}
        >
          {value ?? "—"}
        </span>
      </div>
    </div>
  );
}

// ================================================================
// MINI RIBBON PREVIEW
// ================================================================

const RIBBON_CLIP = "polygon(100% 0, 100% 100%, 0 0)";

function MiniRibbonPreview({
  ribbonText,
  effectiveHex,
}: {
  ribbonText: string;
  effectiveHex: string;
}) {
  return (
    <div className="relative w-[200px] h-[160px] rounded-lg border bg-card overflow-hidden shrink-0">
      {/* Placeholder card body */}
      <div className="p-3 pt-10">
        <div className="w-16 h-3 rounded bg-muted mb-2" />
        <div className="w-24 h-2 rounded bg-muted/60 mb-1.5" />
        <div className="w-20 h-2 rounded bg-muted/40 mb-3" />
        <div className="w-full h-6 rounded bg-muted/30" />
      </div>

      {/* Ribbon (same geometry as RibbonBlock) */}
      <div
        className="absolute top-0 right-0 w-[100px] h-[100px] z-10"
        style={{
          clipPath: RIBBON_CLIP,
          backgroundColor: effectiveHex,
        }}
      >
        <div className="absolute top-3 right-2 text-right text-white">
          <span className="text-[9px] uppercase tracking-wide opacity-90 block font-semibold leading-tight max-w-[55px]">
            {ribbonText || "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

// ================================================================
// MAIN COMPONENT
// ================================================================

export function RibbonDebugSampleCard({
  dealTypeKey,
  ribbonComponentConfig,
  colorConfig,
  graphicSource,
  assetId,
  assetDefaultTint,
  assetName,
  sampleDeal,
}: RibbonDebugSampleCardProps) {
  const dealCategory = dealTypeKey as DealCategory;

  // Resolve color via the same chain as the live renderer
  const resolved = useMemo(
    () => resolveRibbonColor(colorConfig, dealCategory, assetDefaultTint),
    [colorConfig, dealCategory, assetDefaultTint],
  );

  const effectiveHex = useMemo(
    () => getResolvedRibbonHex(colorConfig, dealCategory, assetDefaultTint),
    [colorConfig, dealCategory, assetDefaultTint],
  );

  const themeDefaultHex =
    DEAL_TYPE_DEFAULT_HEX[dealCategory] ??
    DEAL_TYPE_DEFAULT_HEX.UNCLASSIFIED;

  // Build ribbon text view model
  const viewModel = useMemo(
    () => buildRibbonViewModel(sampleDeal ?? null, dealTypeKey, ribbonComponentConfig),
    [sampleDeal, dealTypeKey, ribbonComponentConfig],
  );

  // Diagnostics
  const diagnostics = useMemo(
    () => buildDiagnostics(colorConfig, graphicSource, assetId, assetDefaultTint),
    [colorConfig, graphicSource, assetId, assetDefaultTint],
  );

  // Fill mode label
  const fillModeLabel: Record<RibbonColorSource, string> = {
    custom_hex: "Custom",
    theme_token: "Theme",
    asset_default: "Asset Tint",
  };

  return (
    <div
      className="border rounded-lg bg-muted/20 p-4 space-y-3"
      data-debug-deal-type={dealTypeKey}
      data-debug-fill-mode={colorConfig.color_source}
      data-debug-effective-color={effectiveHex}
      data-debug-graphic-source={graphicSource}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <Info className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold">Debug Ribbon Sample Card</span>
        <Badge variant="outline" className="text-[9px] h-4 px-1.5 ml-auto">
          CR338
        </Badge>
      </div>
      <p className="text-[10px] text-muted-foreground -mt-1">
        Uses the same renderer helpers as live cards. Helpful for verifying
        color mode, text, and asset tint.
      </p>

      {/* Content: mini card + data panel */}
      <div className="flex gap-4 items-start">
        {/* Left: mini ribbon preview */}
        <MiniRibbonPreview
          ribbonText={viewModel.primaryText}
          effectiveHex={effectiveHex}
        />

        {/* Right: data panel */}
        <div className="flex-1 min-w-0 space-y-0.5 pt-0.5">
          <DataRow label="Deal Type" value={dealTypeKey} />
          <DataRow
            label="Ribbon Text (primary)"
            value={viewModel.primaryText || "(empty)"}
          />
          {viewModel.showSubline && (
            <DataRow
              label="Ribbon Text (subline)"
              value={viewModel.merchantName ?? "—"}
            />
          )}

          <div className="border-t my-1.5" />

          <DataRow
            label="Fill Mode"
            value={fillModeLabel[colorConfig.color_source] ?? colorConfig.color_source}
          />
          <DataRow
            label="Effective Color"
            value={effectiveHex}
            mono
            colorSwatch={effectiveHex}
          />
          <DataRow
            label="Theme Default"
            value={themeDefaultHex}
            mono
            colorSwatch={themeDefaultHex}
          />
          {colorConfig.color_source === "custom_hex" && (
            <DataRow
              label="Custom HEX"
              value={colorConfig.color_hex ?? "(not set)"}
              mono
              colorSwatch={
                colorConfig.color_hex
                  ? normalizeHex(colorConfig.color_hex)
                  : null
              }
            />
          )}

          <div className="border-t my-1.5" />

          <DataRow
            label="Graphic"
            value={graphicSource === "asset" ? "Asset Studio" : "System (CSS)"}
          />
          {graphicSource === "asset" && (
            <>
              <DataRow label="Asset" value={assetName ?? assetId ?? "—"} />
              <DataRow
                label="Asset Tint"
                value={assetDefaultTint ? normalizeHex(assetDefaultTint) : "—"}
                mono
                colorSwatch={
                  assetDefaultTint ? normalizeHex(assetDefaultTint) : null
                }
              />
            </>
          )}

          <DataRow
            label="Color Source"
            value={resolved.source}
          />
        </div>
      </div>

      {/* Diagnostics */}
      <div className="space-y-1 pt-1 border-t">
        {diagnostics.map((d, i) => (
          <div
            key={i}
            className={cn(
              "flex items-start gap-1.5 text-[10px]",
              d.severity === "warn"
                ? "text-destructive"
                : "text-muted-foreground",
            )}
          >
            {d.severity === "warn" ? (
              <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
            ) : (
              <CheckCircle2 className="h-3 w-3 shrink-0 mt-0.5" />
            )}
            <span>{d.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default RibbonDebugSampleCard;
